import React, { useContext, useEffect, useState, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import InnerLayout from "../../components/layouts/inner-layout";
import { DataContext } from "../../components/layouts/main-layout";
import TabsComponent from "../../components/helper-components/tabs-component";
import InputField from "../../components/forms/input-field";
import AddButton, { DraftButton } from "../../components/buttons/button";
import CloseIcon from "../../assets/icons/close-icon";
import successToast from "../../components/helper-components/success-toast";
import errorToast from "../../components/helper-components/error-toast";
import EditIcon from "../../assets/icons/action-icons";
import DeleteIcon from "../../assets/icons/delete-icon";
import {
  getFofoStoreTypesApi,
  createFofoStoreTypeApi,
  updateFofoStoreTypeApi,
  deleteFofoStoreTypeApi,
  getFofoFirmTypesApi,
  createFofoFirmTypeApi,
  updateFofoFirmTypeApi,
  deleteFofoFirmTypeApi,
} from "../../redux/apis/store-api";

const SetupDataSettings = () => {
  const { permissionFn } = useContext(DataContext);
  const [currentTab, setCurrentTab] = useState("Store Type");
  const [storeTypes, setStoreTypes] = useState([]);
  const [firmTypes, setFirmTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showStorePanel, setShowStorePanel] = useState(false);
  const [showFirmPanel, setShowFirmPanel] = useState(false);
  const [editingStoreType, setEditingStoreType] = useState(null);
  const [editingFirmType, setEditingFirmType] = useState(null);

  const fetchStoreTypes = () => {
    setLoading(true);
    getFofoStoreTypesApi()
      .then((res) => {
        setStoreTypes(res.data?.data ?? []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const fetchFirmTypes = () => {
    getFofoFirmTypesApi()
      .then((res) => setFirmTypes(res.data?.data ?? []))
      .catch(() => {});
  };

  useEffect(() => { fetchStoreTypes(); }, []);
  useEffect(() => { fetchFirmTypes(); }, []);

  const handleDeleteStoreType = (item) => {
    if (!window.confirm(`Delete store type "${item.store_type}"?`)) return;
    deleteFofoStoreTypeApi({ store_type_id: item.store_type_id })
      .then((res) => {
        successToast(res.data?.message ?? "Store type deleted successfully");
        fetchStoreTypes();
      })
      .catch((err) => {
        errorToast(err?.response?.data?.message ?? "Failed to delete store type");
      });
  };

  const handleDeleteFirmType = (item) => {
    if (!window.confirm(`Delete firm type "${item.firm_type}"?`)) return;
    deleteFofoFirmTypeApi({ firm_type_id: item.firm_type_id })
      .then((res) => {
        successToast(res.data?.message ?? "Firm type deleted successfully");
        fetchFirmTypes();
      })
      .catch((err) => {
        errorToast(err?.response?.data?.message ?? "Failed to delete firm type");
      });
  };

  return (
    <InnerLayout
      permissionFn={permissionFn}
      module="store_document_process"
      title="Setup Data Settings"
    >
      <div className="pt-5 h-full flex flex-col">
        <TabsComponent
          handleClickfn={(item) => setCurrentTab(item)}
          itemTabs={itemTabs}
          setCurrentTab={setCurrentTab}
          currentTab={currentTab}
        />

        <div className="px-[24px] mt-5 flex-1 flex flex-col">
          <div className="flex justify-end mb-3">
            {currentTab === "Store Type" && (
              <AddButton
                text="Add Store Type"
                onclkFn={() => {
                  setEditingStoreType(null);
                  setShowStorePanel(true);
                }}
                prefixIcon={true}
              />
            )}
            {currentTab === "Firm Type" && (
              <AddButton
                text="Add Firm Type"
                onclkFn={() => {
                  setEditingFirmType(null);
                  setShowFirmPanel(true);
                }}
                prefixIcon={true}
              />
            )}
          </div>

          <div className="tab-container outline outline-[var(--list-table-border-color)] outline-1 border-radius overflow-auto">
            {currentTab === "Store Type" && (
              <table className="w-full border-collapse mt-0">
                <thead className="sticky top-0 bg-[var(--list-table-heading-bg)] z-[10]">
                  <tr className="h-[50px]">
                    {storeTypeHeadings.map((h, i) => (
                      <th
                        key={i}
                        className="border-b border-[var(--list-table-border-color)] px-[8px] text-left whitespace-nowrap text-[var(--list-table-heading-fg-color)] text-[length:--list-table-heading-font-size] font-[var(--list-table-heading-font-weight)]"
                      >
                        {h.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={storeTypeHeadings.length} className="px-[8px] h-[50px] text-center text-[var(--list-table-row-fg-color)] text-[length:--list-table-row-font-size]">
                        Loading...
                      </td>
                    </tr>
                  ) : storeTypes.length === 0 ? (
                    <tr>
                      <td colSpan={storeTypeHeadings.length} className="px-[8px] h-[50px] text-center text-[var(--list-table-row-fg-color)] text-[length:--list-table-row-font-size]">
                        No store types found.
                      </td>
                    </tr>
                  ) : (
                    storeTypes.map((item, idx) => (
                      <tr key={idx} className="hover:bg-[var(--list-table-hover-bg)]">
                        <td className="border-b border-[var(--list-table-border-color)] px-[8px] h-[50px] text-[length:--list-table-row-font-size] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)] whitespace-nowrap">{item.store_type ?? "--"}</td>
                        <td className="border-b border-[var(--list-table-border-color)] px-[8px] h-[50px] text-[length:--list-table-row-font-size] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)] whitespace-nowrap">{item.base_commision ?? "--"}</td>
                        <td className="border-b border-[var(--list-table-border-color)] px-[8px] h-[50px] text-[length:--list-table-row-font-size] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)] whitespace-nowrap">{item.additional_margin ?? "--"}</td>
                        <td className="border-b border-[var(--list-table-border-color)] px-[8px] h-[50px] text-[length:--list-table-row-font-size] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)] whitespace-nowrap">{item.capex ?? "--"}</td>
                        <td className="border-b border-[var(--list-table-border-color)] px-[8px] h-[50px] text-[length:--list-table-row-font-size] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)] whitespace-nowrap">{item.franchidse_fee ?? "--"}</td>
                        <td className="border-b border-[var(--list-table-border-color)] px-[8px] h-[50px] text-[length:--list-table-row-font-size] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)] whitespace-nowrap">{item.lock_in_period ?? "--"}</td>
                        <td className="border-b border-[var(--list-table-border-color)] px-[8px] h-[50px] text-[length:--list-table-row-font-size] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)] whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            {permissionFn("store_document_process")?.update && (
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingStoreType(item);
                                  setShowStorePanel(true);
                                }}
                              >
                                <EditIcon />
                              </button>
                            )}
                            {permissionFn("store_document_process")?.delete && (
                              <DeleteIcon onClickFn={() => handleDeleteStoreType(item)} />
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}

            {currentTab === "Firm Type" && (
              <table className="w-full border-collapse mt-0">
                <thead className="sticky top-0 bg-[var(--list-table-heading-bg)] z-[10]">
                  <tr className="h-[50px]">
                    {firmTypeHeadings.map((h, i) => (
                      <th
                        key={i}
                        className="border-b border-[var(--list-table-border-color)] px-[8px] text-left whitespace-nowrap text-[var(--list-table-heading-fg-color)] text-[length:--list-table-heading-font-size] font-[var(--list-table-heading-font-weight)]"
                      >
                        {h.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {firmTypes.length === 0 ? (
                    <tr>
                      <td colSpan={firmTypeHeadings.length} className="px-[8px] h-[50px] text-center text-[var(--list-table-row-fg-color)] text-[length:--list-table-row-font-size]">
                        No firm types found.
                      </td>
                    </tr>
                  ) : (
                    firmTypes.map((item, idx) => (
                      <tr key={idx} className="hover:bg-[var(--list-table-hover-bg)]">
                        <td className="border-b border-[var(--list-table-border-color)] px-[8px] h-[50px] text-[length:--list-table-row-font-size] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)] whitespace-nowrap">{item.firm_type ?? "--"}</td>
                        <td className="border-b border-[var(--list-table-border-color)] px-[8px] h-[50px] text-[length:--list-table-row-font-size] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)] whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            {permissionFn("store_document_process")?.update && (
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingFirmType(item);
                                  setShowFirmPanel(true);
                                }}
                              >
                                <EditIcon />
                              </button>
                            )}
                            {permissionFn("store_document_process")?.delete && (
                              <DeleteIcon onClickFn={() => handleDeleteFirmType(item)} />
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Add Store Type — right slide panel */}
      <Transition.Root show={showStorePanel} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowStorePanel(false)}>
          <div className="fixed inset-0 overflow-hidden">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
              leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
            >
              <Dialog.Overlay className="absolute inset-0 bg-[#0000004D]" />
            </Transition.Child>

            <div className="fixed inset-0 flex justify-end">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full" enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0" leaveTo="translate-x-full"
              >
                <Dialog.Panel className="h-full relative w-[420px] bg-white overflow-y-auto rounded-tl-lg rounded-bl-lg shadow-xl flex flex-col">
                  <Formik
                    enableReinitialize
                    initialValues={editingStoreType ?? storeTypeInitialValues}
                    validationSchema={storeTypeSchema}
                    onSubmit={(values, { setSubmitting, resetForm }) => {
                      const apiCall = editingStoreType
                        ? updateFofoStoreTypeApi(values)
                        : createFofoStoreTypeApi(values);
                      apiCall
                        .then((res) => {
                          successToast(
                            res.data?.message ??
                              (editingStoreType ? "Store type updated successfully" : "Store type added successfully")
                          );
                          fetchStoreTypes();
                          setShowStorePanel(false);
                          setEditingStoreType(null);
                          resetForm();
                        })
                        .catch((err) => {
                          errorToast(err?.response?.data?.message ?? "Failed to save store type");
                        })
                        .finally(() => setSubmitting(false));
                    }}
                  >
                    {({ isSubmitting }) => (
                      <Form className="flex flex-col h-full">
                        {/* Header */}
                        <div
                          className="h-[61px] bg-[var(--slide-panel-head-bg)] px-4 flex items-center justify-between border-b flex-shrink-0"
                          style={{ borderColor: "var(--slide-panel-border)" }}
                        >
                          <span
                            style={{
                              color: "var(--slide-panel-h-fg)",
                              fontSize: "var(--slide-panel-h-fs)",
                              fontWeight: "var(--slide-panel-h-fw)",
                            }}
                          >
                            {editingStoreType ? "Edit Store Type" : "Add Store Type"}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              setShowStorePanel(false);
                              setEditingStoreType(null);
                            }}
                          >
                            <CloseIcon />
                          </button>
                        </div>

                        {/* Body */}
                        <div className="flex-1 overflow-y-auto px-5 py-5">
                          <InputField name="store_type" label="Store Type" placeholder="Enter store type" isRequired labelWidth="11rem" />
                          <InputField name="base_commision" label="Base Commission" placeholder="Enter base commission" labelWidth="11rem" />
                          <InputField name="additional_margin" label="Additional Margin" placeholder="Enter additional margin" labelWidth="11rem" />
                          <InputField name="capex" label="Approx Capex" placeholder="Enter capex" labelWidth="11rem" />
                          <InputField name="franchidse_fee" label="Franchise Fee" placeholder="Enter franchise fee" labelWidth="11rem" />
                          <InputField name="lock_in_period" label="Lock-in Period" placeholder="Enter lock-in period" labelWidth="11rem" />
                        </div>

                        {/* Footer */}
                        <div className="flex justify-between items-center px-5 py-4 border-t border-[var(--slide-panel-border)] flex-shrink-0">
                          <DraftButton
                            text="Cancel"
                            onclkFn={() => {
                              setShowStorePanel(false);
                              setEditingStoreType(null);
                            }}
                          />
                          <AddButton text="Save" type="submit" prefixIcon={false} loading={isSubmitting} />
                        </div>
                      </Form>
                    )}
                  </Formik>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Add Firm Type — right slide panel */}
      <Transition.Root show={showFirmPanel} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => {
            setShowFirmPanel(false);
            setEditingFirmType(null);
          }}
        >
          <div className="fixed inset-0 overflow-hidden">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
              leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
            >
              <Dialog.Overlay className="absolute inset-0 bg-[#0000004D]" />
            </Transition.Child>

            <div className="fixed inset-0 flex justify-end">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full" enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0" leaveTo="translate-x-full"
              >
                <Dialog.Panel className="h-full relative w-[420px] bg-white overflow-y-auto rounded-tl-lg rounded-bl-lg shadow-xl flex flex-col">
                  <Formik
                    enableReinitialize
                    initialValues={editingFirmType ?? { firm_type: "" }}
                    validationSchema={Yup.object({ firm_type: Yup.string().required("Firm type is required") })}
                    onSubmit={(values, { setSubmitting, resetForm }) => {
                      const apiCall = editingFirmType
                        ? updateFofoFirmTypeApi(values)
                        : createFofoFirmTypeApi(values);
                      apiCall
                        .then((res) => {
                          successToast(
                            res.data?.message ??
                              (editingFirmType ? "Firm type updated successfully" : "Firm type added successfully")
                          );
                          fetchFirmTypes();
                          setShowFirmPanel(false);
                          setEditingFirmType(null);
                          resetForm();
                        })
                        .catch((err) => {
                          errorToast(err?.response?.data?.message ?? "Failed to save firm type");
                        })
                        .finally(() => setSubmitting(false));
                    }}
                  >
                    {({ isSubmitting }) => (
                      <Form className="flex flex-col h-full">
                        {/* Header */}
                        <div
                          className="h-[61px] bg-[var(--slide-panel-head-bg)] px-4 flex items-center justify-between border-b flex-shrink-0"
                          style={{ borderColor: "var(--slide-panel-border)" }}
                        >
                          <span
                            style={{
                              color: "var(--slide-panel-h-fg)",
                              fontSize: "var(--slide-panel-h-fs)",
                              fontWeight: "var(--slide-panel-h-fw)",
                            }}
                          >
                            {editingFirmType ? "Edit Firm Type" : "Add Firm Type"}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              setShowFirmPanel(false);
                              setEditingFirmType(null);
                            }}
                          >
                            <CloseIcon />
                          </button>
                        </div>

                        {/* Body */}
                        <div className="flex-1 overflow-y-auto px-5 py-5">
                          <InputField name="firm_type" label="Firm Type" placeholder="Enter firm type" isRequired labelWidth="8rem" />
                        </div>

                        {/* Footer */}
                        <div className="flex justify-between items-center px-5 py-4 border-t border-[var(--slide-panel-border)] flex-shrink-0">
                          <DraftButton
                            text="Cancel"
                            onclkFn={() => {
                              setShowFirmPanel(false);
                              setEditingFirmType(null);
                            }}
                          />
                          <AddButton text="Save" type="submit" prefixIcon={false} loading={isSubmitting} />
                        </div>
                      </Form>
                    )}
                  </Formik>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </InnerLayout>
  );
};

export default SetupDataSettings;

const itemTabs = ["Store Type", "Firm Type"];

const storeTypeHeadings = [
  { label: "STORE TYPE" },
  { label: "BASE COMMISSION" },
  { label: "ADDITIONAL MARGIN" },
  { label: "APPROX CAPEX" },
  { label: "FRANCHISE FEE" },
  { label: "LOCK-IN PERIOD" },
  { label: "ACTIONS" },
];

const firmTypeHeadings = [
  { label: "FIRM TYPE" },
  { label: "ACTIONS" },
];

const storeTypeInitialValues = {
  store_type: "",
  base_commision: "",
  additional_margin: "",
  capex: "",
  franchidse_fee: "",
  lock_in_period: "",
};

const storeTypeSchema = Yup.object({
  store_type: Yup.string().required("Store type is required"),
});

 
